import type {
  RecordingIntakeAgentMapping,
  RecordingIntakeAgentMappingResponse,
  RecordingIntakeCustomerFailuresCount,
  RecordingIntakeCustomerFailuresCountResponse,
  RecordingIntakeCustomerFailuresPage,
  RecordingIntakeCustomerFailuresPageResponse,
  RecordingIntakeCustomerReprocessResponse,
  RecordingIntakeCustomerReprocessResult,
  RecordingIntakeCustomerSkipResponse,
  RecordingIntakeCustomerSkipResult,
  RecordingIntakeId,
  RecordingIntakeSortOrder,
} from "@repo/app-types/entities/recordingIntake";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The customer-facing "Recording S3 intake" surface. The
 * integration itself rides the boot payload as a synthetic row (see
 * `selectRecordingS3IntakeIntegration`); these endpoints back the Failed imports
 * tab and the Settings tab's agent-email mapping. Mirrors the Genesys customer
 * endpoints (`genesysIntegration.ts`).
 */

// The failed-imports list + count share this tag, so reprocessing or skipping a
// row refetches both. The agent-email map is read-only and never invalidated.
const FAILED_IMPORTS_TAG_ID = "recording-s3-intake-failed-imports";
const AGENT_MAP_TAG_ID = "recording-s3-intake-agent-map";

export const recordingS3IntakeIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // The Failed imports tab: the caller's own `failed` rows, company-scoped by
    // the server. The only arg is the `intaken_at` sort order, part of the cache
    // key so flipping it starts a fresh page 1.
    recordingS3IntakeFailedImports: builder.infiniteQuery<
      RecordingIntakeCustomerFailuresPage,
      { order: RecordingIntakeSortOrder },
      string | null,
      RecordingIntakeCustomerFailuresPageResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/integrations/recording-s3-intake/failed-imports",
        params: {
          order: queryArg.order,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The failed count, for the tab badge — its own read so a page of the list
    // isn't needed to know whether the badge should show.
    recordingS3IntakeFailedImportsCount: builder.query<
      RecordingIntakeCustomerFailuresCount,
      void
    >({
      query: () => "/integrations/recording-s3-intake/failed-imports/count",
      transformResponse: (
        response: RecordingIntakeCustomerFailuresCountResponse,
      ) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    reprocessRecordingS3IntakeFailedImports: builder.mutation<
      RecordingIntakeCustomerReprocessResult,
      readonly RecordingIntakeId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/recording-s3-intake/failed-imports/reprocess",
        body: { ids },
      }),
      transformResponse: (response: RecordingIntakeCustomerReprocessResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    skipRecordingS3IntakeFailedImports: builder.mutation<
      RecordingIntakeCustomerSkipResult,
      readonly RecordingIntakeId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/recording-s3-intake/failed-imports/skip",
        body: { ids },
      }),
      transformResponse: (response: RecordingIntakeCustomerSkipResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The read-only RingCX agent username → teammate email map (RingCX customers
    // only). No live-refresh action, unlike the HQ tooling twin.
    recordingS3IntakeAgentEmailMap: builder.query<
      RecordingIntakeAgentMapping,
      void
    >({
      query: () => "/integrations/recording-s3-intake/agent-email-map",
      transformResponse: (response: RecordingIntakeAgentMappingResponse) =>
        response.data,
      providesTags: () => [
        buildEntityTag(TagType.RecordingS3IntakeIntegration, AGENT_MAP_TAG_ID),
      ],
    }),
  }),
});

export const {
  useRecordingS3IntakeFailedImportsInfiniteQuery,
  useRecordingS3IntakeFailedImportsCountQuery,
  useReprocessRecordingS3IntakeFailedImportsMutation,
  useSkipRecordingS3IntakeFailedImportsMutation,
  useRecordingS3IntakeAgentEmailMapQuery,
} = recordingS3IntakeIntegrationApi;
